import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import type { EventCategory } from '#~/analytics/constants/event-categories.js';
import type { EventName } from '#~/analytics/constants/event-names.js';
import { buildClaimAnalyticsProps } from '#~/analytics/domains/claim-identity.js';
import {
  armDeviceShadowTimeout,
  shouldArmDeviceShadowTimeout
} from '#~/analytics/domains/shadow-timeout.js';
import type { EventProperties } from '#~/analytics/types/properties.js';

type TrackFn = (name: EventName, category: EventCategory, properties: EventProperties) => void;

/** The parts of the registry link reply the claim screen reads. */
export type ClaimLinkResponse = {
  error?: { code?: number; message?: string } | undefined;
  deviceLoginLockoutStatus?: { locked: boolean; unlockTimeSeconds: number } | undefined;
};

/** What the claim screen shows for one link reply. */
export type ClaimLinkOutcome =
  | { kind: 'locked'; unlockTimeSeconds: number }
  | { kind: 'auth-error'; message: string }
  | { kind: 'agent-error'; message: string | undefined }
  | { kind: 'linked' };

/**
 * Classify one claim-link reply and report a rejected credential.
 *
 * WHY here and not inline in `auth.tsx`: the screen's reply handling is the
 * authoritative claim boundary, and the e2e receipt drives this same function,
 * so deleting the `device_auth_invalid` emit reds the receipt as well as the
 * unit test.
 *
 * Only the 401 is reported, because that is what `device_auth_invalid` means
 * to the saved production reports that count it with no filter (auth-failure
 * rate, add-camera-failed), and only an unlocked 401 proves a password was
 * checked and rejected. Any locked reply, the 400, a missing agent and other
 * link errors keep their screens and send nothing, so those reports keep
 * counting the same thing they always did. `agent_id`
 * always comes from the route, even before the agent record loads.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export const resolveClaimLinkResponse = (input: {
  response: ClaimLinkResponse;
  agentId: string;
  studioId?: string;
  embedded?: boolean;
  track: TrackFn;
}): ClaimLinkOutcome => {
  const { response, track } = input;
  const reportRejected = (extra: EventProperties): void => {
    track(EVENT_NAMES.DEVICE_AUTH_INVALID, EVENT_CATEGORIES.DEVICE, {
      ...extra,
      deviceType: input.embedded ? 'embedded' : 'agent',
      ...buildClaimAnalyticsProps({
        agentId: input.agentId,
        ...(input.studioId !== undefined ? { studioId: input.studioId } : {}),
        ...(typeof input.embedded === 'boolean' ? { embedded: input.embedded } : {})
      })
    });
  };

  // A locked reply is never reported as a rejected password. The bridge sends
  // the same 401 "Unauthorized" with no attempts left whether this password
  // locked the camera or the camera was already locked and never checked it,
  // so the reply cannot prove a password was rejected. This also leaves out the
  // one wrong password that triggers the lock; old firmware gives no way to
  // tell it apart. Saved auth-failure reports never counted locked replies.
  if (response.deviceLoginLockoutStatus?.locked) {
    return {
      kind: 'locked',
      unlockTimeSeconds: response.deviceLoginLockoutStatus.unlockTimeSeconds
    };
  }

  switch (response.error?.code) {
    case undefined:
      if (response.error === undefined) {
        return { kind: 'linked' };
      }
      return { kind: 'agent-error', message: response.error.message };
    case 400:
      return { kind: 'auth-error', message: 'Invalid credentials provided.' };
    case 401:
      reportRejected({
        errorCode: 401,
        errorMessage: response.error?.message || 'Unauthorized'
      });
      return { kind: 'auth-error', message: 'Unauthorized. Please check your credentials.' };
    case 404:
      return { kind: 'agent-error', message: 'Agent not found.' };
    default:
      return { kind: 'agent-error', message: response.error?.message };
  }
};

/**
 * Report a settled claim and start its claim-without-shadow clock.
 *
 * WHY here: `device_authenticate` and the clock are the claim screen's two
 * success-side signals, and the e2e receipt calls this same function, so
 * dropping either one from the screen's success path reds a test instead of
 * passing silently. The clock starts after settlement because bootstrap is what
 * emits `device_shadowed`, and it has not started yet. Regular agents never
 * shadow, so they are not armed.
 */
export const reportClaimLinked = (input: {
  agentId: string;
  studioId?: string;
  embedded?: boolean;
  track: TrackFn;
}): void => {
  input.track(EVENT_NAMES.DEVICE_AUTHENTICATE, EVENT_CATEGORIES.DEVICE, {
    deviceType: input.embedded === true ? 'embedded' : 'agent',
    ...buildClaimAnalyticsProps({
      agentId: input.agentId,
      ...(input.studioId !== undefined ? { studioId: input.studioId } : {}),
      ...(typeof input.embedded === 'boolean' ? { embedded: input.embedded } : {})
    })
  });
  if (shouldArmDeviceShadowTimeout(input.embedded)) {
    armDeviceShadowTimeout({
      agentId: input.agentId,
      ...(input.studioId !== undefined ? { studioId: input.studioId } : {}),
      track: input.track
    });
  }
};
